import * as React from "react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./card"
import { cn } from "@/lib/utils"

export interface InteractiveCardProps extends React.HTMLAttributes<HTMLDivElement> {
  hover?: boolean
  clickable?: boolean
  glow?: boolean
  lift?: boolean
  pulse?: boolean
}

const InteractiveCard = React.forwardRef<HTMLDivElement, InteractiveCardProps>(
  ({ className, hover = true, clickable = false, glow = false, lift = true, pulse = false, children, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        className={cn(
          "transition-all duration-200 transform-gpu",
          hover && "hover:scale-[1.02]",
          lift && "hover:micro-lift",
          glow && "hover:micro-glow",
          pulse && "micro-pulse",
          clickable && "cursor-pointer active:scale-[0.98]",
          className
        )}
        {...props}
      >
        {children}
      </Card>
    )
  }
)

InteractiveCard.displayName = "InteractiveCard"

// Export card components with enhanced versions
const InteractiveCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <CardHeader
    ref={ref}
    className={cn("transition-colors duration-200", className)}
    {...props}
  />
))
InteractiveCardHeader.displayName = "InteractiveCardHeader"

const InteractiveCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <CardTitle
    ref={ref}
    className={cn("transition-colors duration-200 hover:text-primary", className)}
    {...props}
  />
))
InteractiveCardTitle.displayName = "InteractiveCardTitle"

const InteractiveCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <CardDescription
    ref={ref}
    className={cn("transition-colors duration-200", className)}
    {...props}
  />
))
InteractiveCardDescription.displayName = "InteractiveCardDescription"

const InteractiveCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <CardContent
    ref={ref}
    className={cn("transition-all duration-200", className)}
    {...props}
  />
))
InteractiveCardContent.displayName = "InteractiveCardContent"

const InteractiveCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <CardFooter
    ref={ref}
    className={cn("transition-all duration-200", className)}
    {...props}
  />
))
InteractiveCardFooter.displayName = "InteractiveCardFooter"

export {
  InteractiveCard,
  InteractiveCardHeader,
  InteractiveCardTitle,
  InteractiveCardDescription,
  InteractiveCardContent,
  InteractiveCardFooter,
}